import React, { useState, useCallback, useEffect, useRef } from 'react';
import { CircularProgress, Drawer } from '@mui/material';
import { Box, Typography, Grid } from '@availity/element';
import { useNavigate } from 'react-router-dom';
import Button from '../../Utils/components/Button';
import Sidebar from './Sidebar';
import { ValueCard, InfoCard, ApiCard, QuickstartStep, DemoFlowCard, FeatureItem } from '../../Utils/components/Cards';
import { typographyStyles, boxStyles, themeColors } from '../../../static/constants/styles';
import { colors } from '../../../static/constants/theme';
import { typography } from '../../../static/constants/typography';
import useMcpTools from '../../../hooks/useMcpTools';
import ErrorBoundary from '../../ErrorBoundary';
import { McpToolParameter } from '../../../api/mcpToolsApi';
import hospitalUserIcon from '../../../static/assets/icons/hospital-user.svg';
import moneyBillIcon from '../../../static/assets/icons/money-bill.svg';
import codeIcon from '../../../static/assets/icons/code.svg';
import checkOrangeIcon from '../../../static/assets/icons/check-orange.svg';

const CURL_EXAMPLE = `curl --request POST
  --url https://preview.api.availity.com/mcp/messages
  --header 'Accept: application/json'
  --header 'Authorization: Bearer <ACCESS_TOKEN>'
  --header 'Content-Type: application/json'
  --header 'x-api-key: <API_KEY>'
  --data '{
	"jsonrpc": "2.0",
	"id": 5,
	"method": "tools/call",
	"params": {
		"name": "verify_coverage",
		"arguments": {
			"request": {
				"cardIssueDate": "2024-01-01T00:00:00Z",
				"coverageEffectiveDate": "2024-01-01T00:00:00Z",
				"coverageEndDate": "2024-12-31T23:59:59Z",
				"memberIdentifier": "123456789",
				"patientBirthDate": "1990-01-15",
				"patientFirstName": "John",
				"patientLastName": "Doe",
				"payerIdentifier": "AVPAYER",
				"placeOfService": "11",
				"procedureCode": "99213",
				"providerNpi": "2154879635",
				"providerSsn": "1194",
				"serviceType": [
					"30"
				],
				"submitterIdentifier": "AvailityHealth"
			}
		}
	}
}'`;

const VALUE_CARDS = [
  { badge: 'Tool discovery built in', title: 'Designed for developer tooling', description: 'Exposes capabilities as discoverable tools, so automation, assistants, and scripted workflows are easier to build.' },
  { badge: 'Structured responses', title: 'Clear, actionable outputs', description: 'Standardized response objects and errors reduce time spent debugging and integrating.' },
  { badge: 'Coverages - Claims - Estimator', title: 'Composable Workflows', description: 'Supports more business-aligned interactions instead of low-level API calls.' },
];

const INFO_CARDS = [
  { icon: hospitalUserIcon, title: 'MCP Host', description: "The Large Language Model (LLM) is contained within the MCP Host or within an AI application or environment, such as an AI-powered IDE or conversational AI. As a user's typical interaction point, this is where the MCP Host uses the LLM to process requests that may require external data or tools." },
  { icon: moneyBillIcon, title: 'MCP Client', description: "The MCP Client, located within the MCP Host, helps the LLM and MCP Server communicate with each other. It translates the LLM's requests for the MCP and converts the MCP's replies for the LLM. It also finds and uses available MCP Servers." },
  { icon: codeIcon, title: 'MCP Server', description: 'The MCP Server is the external service that provides context, data, or capabilities to the LLM. It enables LLMs to connect to external systems, like databases and web services, translating their responses into a format the LLM can understand, which helps developers provide diverse functionalities.' },
];

const FEATURE_ITEMS = [
  'Capability discovery built in: Programmatically list available tools, required inputs, and supported parameters.',
  'Predictable request patterns: Consistent payload structure makes calls easier to automate, test, and reuse.',
  'Structured responses & errors: Clear outputs and standardized error objects support resilient integrations and faster troubleshooting.',
  'Workflow-friendly by design: Compose multi-step journeys—coverage → estimate without reworking integration patterns.',
  'Documentation that matches behavior: Examples map directly to tool schemas, so developers can copy, run, and iterate quickly.',
];

const QUICKSTART_STEPS = [
  { number: '1', title: 'Sign Up for Early Access to receive access details and onboarding instructions', description: 'Register for early access to the MCP Server and receive your API credentials and onboarding documentation.' },
  { number: '2', title: 'Configure and authenticate your MCP Client', description: "Configure your MCP Client to connect to Availity's MCP Server and authenticate using an API key for seamless integration." },
  { number: '3', title: 'Discover available tools to find the capability you need', description: 'List tools using MCP method tools/list to find the capability you need.' },
  { number: '4', title: 'Invoke a tool using the standard call pattern', description: 'Make your first API call using the discovered tool name and required input parameters.' },
  { number: '5', title: 'Add context to your MCP Client so it can orchestrate MCP tools end-to-end', description: 'Provide the necessary context to your MCP Client so it can integrate and orchestrate MCP tools within your workflow.' },
];

const GRID_LAYOUT = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' },
  gap: 3,
  maxWidth: 1200,
  width: '100%',
};

const McpToolsErrorThrower = ({ error }: { error: string | null }) => {
  if (error) throw new Error(error);
  return null;
};

const AIManagementTab = ({ theme, sidebarOpen, onSidebarClose }: { theme: 'light' | 'dark'; sidebarOpen: boolean; onSidebarClose: () => void }) => {
  const navigate = useNavigate();
  // activeSection drives which body content is shown: 'mcp-server' or 'mcp-tools'
  const [activeSection, setActiveSection] = useState<string>('mcp-server');
  const [expandedSection, setExpandedSection] = useState<Set<string>>(new Set(['mcp-server']));

  const NestedParamTable = ({ params }: { params: McpToolParameter[] }) => {
    const hasNested = params.some((p) => p.properties?.length);
    const cols = hasNested ? ['Name', 'Type', 'Description'] : ['Name', 'Type', 'Description'];
    return (
      <Grid className="ai-management-tab__param-table" sx={{ border: `1px solid ${themeColors[theme].border}` }}>
        <Grid className="ai-management-tab__param-table-header" sx={{ bgcolor: themeColors[theme].cardBackground, borderBottom: `1px solid ${themeColors[theme].border}` }}>
          {cols.map((col, i) => (
            <Grid key={col} className={`ai-management-tab__param-table-header-cell ai-management-tab__param-table-header-cell--${col === 'Description' ? 'desc' : col === 'Type' ? 'type' : 'name'}`} sx={{ borderRight: i < cols.length - 1 ? `1px solid ${themeColors[theme].border}` : 'none', ...(col === 'Name' && hasNested ? { paddingLeft: '48px' } : {}) }}>
              <Typography sx={{ ...typography.latoSemibold14, color: themeColors[theme].text }}>{col}</Typography>
            </Grid>
          ))}
        </Grid>
        {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
        <ParameterRows params={params} hasNested={hasNested} />
      </Grid>
    );
  };

  const ParameterRows = ({ params, hasNested }: { params: McpToolParameter[]; hasNested: boolean }) => {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    return (
      <>
        {params.map((param, idx, arr) => (
          <React.Fragment key={param.name}>
            <Grid
              className="ai-management-tab__param-table-row"
              onClick={param.properties?.length ? () => setExpanded((prev) => ({ ...prev, [param.name]: !prev[param.name] })) : undefined}
              sx={{
                bgcolor: idx % 2 === 0 ? themeColors[theme].background : themeColors[theme].cardBackground,
                borderBottom: idx < arr.length - 1 ? `1px solid ${themeColors[theme].border}` : 'none',
                cursor: param.properties?.length ? 'pointer' : 'default',
                '&:hover': param.properties?.length ? { bgcolor: themeColors[theme].cardBackground } : {},
              }}
            >
              {param.properties?.length ? (
                <Grid className="ai-management-tab__param-table-cell ai-management-tab__param-table-cell--arrow" sx={{ borderRight: `1px solid ${themeColors[theme].border}` }}>
                  <Box component="span" className="ai-management-tab__param-arrow" sx={{ color: themeColors[theme].text, transform: expanded[param.name] ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</Box>
                </Grid>
              ) : null}
              <Grid className="ai-management-tab__param-table-cell ai-management-tab__param-table-cell--name" sx={{ borderRight: `1px solid ${themeColors[theme].border}`, ...(hasNested && !param.properties?.length ? { marginLeft: '32px' } : {}) }}>
                <Typography sx={{ ...typography.latoRegular14Alt, color: themeColors[theme].text, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{param.name}</Typography>
              </Grid>
              <Grid className="ai-management-tab__param-table-cell ai-management-tab__param-table-cell--type" sx={{ borderRight: `1px solid ${themeColors[theme].border}` }}>
                <Typography sx={{ ...typography.latoRegular14Alt, color: themeColors[theme].textMuted }}>{param.type}</Typography>
              </Grid>
              <Grid className="ai-management-tab__param-table-cell ai-management-tab__param-table-cell--desc">
                <Typography sx={{ ...typography.latoRegular14Alt, color: themeColors[theme].textMuted, wordBreak: 'break-word' }}>{param.description}</Typography>
              </Grid>
            </Grid>
            {param.properties?.length && expanded[param.name] ? (
              <Grid className="ai-management-tab__param-nested-wrapper" sx={{ borderBottom: idx < arr.length - 1 ? `1px solid ${themeColors[theme].border}` : 'none', bgcolor: themeColors[theme].background }}>
                <NestedParamTable params={param.properties} />
              </Grid>
            ) : null}
          </React.Fragment>
        ))}
      </>
    );
  };
  const [activeContent, setActiveContent] = useState<string>('model-context-protocol');
  const { tools: mcpToolCategories, loading: mcpLoading, error: mcpError, fetch: fetchMcpTools } = useMcpTools();
  const contentBoxRef = useRef<HTMLDivElement>(null);
  const isProgrammaticScrollRef = useRef<boolean>(false);

  const handleMenuItemClick = useCallback((contentId: string) => {
    // Determine which section this item belongs to and switch content accordingly
    const MCP_SERVER_IDS = ['model-context-protocol', 'key-mcp-features', 'what-is-mcp', 'availity-mcp-server', 'why-choose-mcp', 'supported-apis', 'getting-started-with-mcp', 'example-workflow'];
    const newSection = MCP_SERVER_IDS.includes(contentId) ? 'mcp-server' : 'mcp-tools';
    setActiveSection(newSection);
    setActiveContent(contentId);
    isProgrammaticScrollRef.current = true;
    setTimeout(() => {
      const target = document.getElementById(contentId);
      if (target) {
        const onScrollEnd = () => {
          isProgrammaticScrollRef.current = false;
          window.removeEventListener('scrollend', onScrollEnd);
        };
        window.addEventListener('scrollend', onScrollEnd, { once: true });
        // Fallback timeout in case scrollend doesn't fire (e.g., already at position)
        setTimeout(() => {
          isProgrammaticScrollRef.current = false;
          window.removeEventListener('scrollend', onScrollEnd);
        }, 1000);
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        isProgrammaticScrollRef.current = false;
      }
    }, 50);
  }, []);

  const handleToggleSection = useCallback((section: string) => {
    setExpandedSection((prev) => {
      const next = new Set(prev);
      if (next.has(section)) { next.delete(section); } else { next.add(section); }
      return next;
    });
    setActiveSection(section);
    if (section === 'mcp-tools') fetchMcpTools();
    if (section === 'mcp-server') setActiveContent('model-context-protocol');
  }, [fetchMcpTools]);

  // Scroll-sync is handled by useScrollSync inside PageSidebar — no duplicate listener needed here.

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const renderMcpServerContent = () => (
    <Box sx={{ width: '100%', maxWidth: 1200 }}>
      <Box id="model-context-protocol">
        <Typography sx={{ mb: 2, color: themeColors[theme].textMuted, fontSize: '16px', fontFamily: 'Lato', fontWeight: 600, lineHeight: '100%' }}>
          Model Context Protocol
        </Typography>
        <Typography component="h1" sx={{ ...typographyStyles.h1, mb: 3, color: themeColors[theme].textSecondary }}>
          Model Context Protocol Server for Availity APIs
        </Typography>
        <Typography sx={{ ...typographyStyles.body, mb: 6, ...boxStyles.section, color: themeColors[theme].textSecondary }}>
          A MCP Server provides an interaction layer over Availity APIs that allows clients to explore available capabilities, invoke supported actions, and receive standardized responses through a consistent MCP protocol, making Availity services easier to discover, integrate, and automate.
        </Typography>
      </Box>

      <Box id="key-mcp-features">
        <Typography component="h2" sx={{ ...typographyStyles.h2, mt: 8, mb: 4, color: themeColors[theme].textHeading }}>Key MCP Features</Typography>
        <Box sx={GRID_LAYOUT}>
          {VALUE_CARDS.map((card) => <ValueCard key={card.title} {...card} theme={theme} />)}
        </Box>
      </Box>

      <Box id="what-is-mcp">
        <Typography component="h2" sx={{ ...typographyStyles.h2, mt: 8, mb: 3, color: themeColors[theme].textHeading }}>What is MCP?</Typography>
        <Typography sx={{ ...typographyStyles.body, mb: 6, ...boxStyles.section, color: themeColors[theme].textBody }}>
          The Model Context Protocol (MCP) is a standardized protocol that enables AI models to interact with external tools and resources in a structured way. Think of it as a bridge between your AI models and the real-world, allowing your AI models to access databases, APIs, file systems, and other external services through a consistent interface. It supports multiple transport mechanisms to provide flexibility across different environments.
        </Typography>
        <Box sx={GRID_LAYOUT}>
          {INFO_CARDS.map((card) => <InfoCard key={card.title} {...card} theme={theme} />)}
        </Box>
      </Box>

      <Box id="availity-mcp-server">
        <Typography component="h2" sx={{ ...typographyStyles.h2, mt: 8, mb: 3, color: themeColors[theme].textBody }}>Availity MCP Server</Typography>
        <Typography component="h3" sx={{ ...typographyStyles.h3, mb: 2, color: themeColors[theme].textSubheading }}>What is available to you via Availity MCP Server?</Typography>
        <Typography sx={{ ...typographyStyles.body, mb: 3, maxWidth: 1200, color: themeColors[theme].textBody }}>
          The MCP Server is Availity&apos;s Model Context Protocol endpoint that exposes API capabilities as discoverable tools. Your MCP Client can list tools, inspect required inputs, and invoke actions using a consistent call pattern.
        </Typography>
        <Typography sx={{ ...typographyStyles.body, mb: 6, maxWidth: 1200, color: themeColors[theme].textBody }}>
          Instead of learning different conventions across services, MCP provides a surface that helps teams build repeatable workflows spanning Coverages, Patient Cost Estimator, and Claims etc.
        </Typography>
      </Box>

      <Box id="why-choose-mcp">
        <Typography component="h2" sx={{ ...typographyStyles.h2, mt: 8, mb: 3, color: themeColors[theme].textBody }}>Why choose MCP?</Typography>
        <Typography sx={{ ...typographyStyles.body, mb: 4, maxWidth: 1200, color: themeColors[theme].textBody }}>
          MCP standardizes how capabilities are discovered and invoked, so integrations are faster to implement and easier to maintain.
        </Typography>
        <Box sx={{ maxWidth: 1200, mb: 8 }}>
          {FEATURE_ITEMS.map((text) => <FeatureItem key={text.slice(0, 30)} text={text} isLast={text === FEATURE_ITEMS[FEATURE_ITEMS.length - 1]} theme={theme} />)}
        </Box>
      </Box>

      <Box id="supported-apis">
        <Typography component="h2" sx={{ ...typographyStyles.h2, mt: 8, mb: 3, color: themeColors[theme].textBody }}>Supported APIs</Typography>
        <Typography sx={{ ...typographyStyles.subtitle, mb: 6, maxWidth: 1200, color: themeColors[theme].textSubheading }}>
          Use MCP tools to interact with the most common workflows across three core Availity APIs.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', xl: 'row' }, gap: 2, width: '100%', maxWidth: 1200 }}>
          <ApiCard title="Coverages" description="Eligibility and benefits workflows are exposed as MCP tools, so you can validate coverage with consistent inputs and outputs." items={['Eligibility checks and coverage validation', 'Benefit details and member context', 'Common "first call" patterns']} buttonText="Explore API" onButtonClick={() => navigate('/docs/overview', { state: { scrollToTag: 'coverages' } })} theme={theme} />
          <ApiCard title="Patient Cost Estimator" description="Create estimate workflows with structured tool outputs that support transparency and orchestration, shifting data quality left and enabling pre-service workflow enablement." items={['Estimate requests for services and members', 'Structured cost breakdown outputs', 'Workflow-ready responses']} buttonText="Explore API" onButtonClick={() => navigate('/docs/overview', { state: { scrollToTag: 'patient-cost-estimator' } })} theme={theme} />
          <ApiCard title="Claim Statuses" description="Claim status workflows are represented as MCP tools that surface the lifecycle and adjudication state of a claim, enabling consumers to retrieve timely status, payment, and exception details through structured tool inputs and predictable outputs." items={['Claim status lookup and tracking', 'Adjudication and lifecycle details', 'Service line\u2013level status']} buttonText="Explore API" onButtonClick={() => navigate('/docs/overview', { state: { scrollToTag: 'claim-status' } })} theme={theme} />
        </Box>
      </Box>

      <Box id="getting-started-with-mcp">
        <Typography component="h2" sx={{ ...typographyStyles.h2, mt: 8, mb: 3, color: themeColors[theme].textBody }}>Getting Started with MCP</Typography>
        <Typography sx={{ ...typographyStyles.bodyBold, mb: 6, maxWidth: 1200, color: themeColors[theme].textBody }}>Go from discovery to first successful call using a single MCP Server pattern.</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', xl: 'row' }, gap: 2.5, width: '100%', maxWidth: 1200 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {QUICKSTART_STEPS.map((step) => <QuickstartStep key={step.number} {...step} theme={theme} />)}
          </Box>
          <Box sx={{ ...boxStyles.codeBox, flex: 1, alignSelf: 'flex-start', border: `1px solid ${themeColors[theme].border}` }}>
            <Box sx={{ ...boxStyles.codeHeader, bgcolor: themeColors[theme].codeBackground }}>
              <Typography sx={{ ...typographyStyles.body, color: themeColors[theme].text }}>Example Invocation</Typography>
            </Box>
            <Box component="pre" sx={{ ...boxStyles.codeContent, width: '100%', color: themeColors[theme].codeText, borderTop: `1px solid ${themeColors[theme].border}`, whiteSpace: 'pre-wrap', overflow: 'visible', m: 0 }}>
{CURL_EXAMPLE}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box id="example-workflow" sx={{ width: '100%', maxWidth: 1200, display: 'flex', flexDirection: 'column', gap: 4, mt: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography component="h2" sx={{ ...typographyStyles.h2, color: themeColors[theme].textBody }}>Example workflow: Coverage → Estimate</Typography>
          <Typography sx={{ ...typographyStyles.body, maxWidth: 1200, color: themeColors[theme].textBody }}>The following is a practical workflow showing how MCP can orchestrate multiple Availity API capabilities using one invocation pattern.</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', xl: 'row' }, gap: 2, width: '100%', maxWidth: 1200 }}>
          <DemoFlowCard number="1" title="Verify coverage" description="Confirm eligibility and benefits for the member and payer context" tags={['coverageStatus', 'planDetails', 'benefitSummary']} theme={theme} />
          <DemoFlowCard number="2" title="Generate an estimate" description="Retrieve a structured estimate response for the requested service." tags={['estimateTotal', 'lineItems', 'memberResponsibility']} theme={theme} />
        </Box>
        <Box component="video" controls preload="metadata" title="API Marketplace MCP Coverages Demo" src="/static/assets/videos/API-Marketplace_MCP-Coverages-Demo.mp4" sx={{ width: '100%', maxWidth: 1200, aspectRatio: '16 / 9', borderRadius: '8px', border: `6px solid ${themeColors[theme].videoBorder}`, display: 'block' }} />
      </Box>

      <Box sx={{ width: '100%', maxWidth: 1200, display: 'flex', flexDirection: 'column', gap: 5, padding: '40px 0', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography sx={{ ...typographyStyles.cta, color: themeColors[theme].textBodyWhite }}>Sign Up for Early Access</Typography>
          <Typography sx={{ ...typographyStyles.bodyLarge, maxWidth: 1200, color: themeColors[theme].textBodyWhite }}>Sign up to receive access details and onboarding instructions for the MCP Server experience.</Typography>
        </Box>
        <Button variant="navbar" onClick={() => navigate('/join-early')}>Get Early Access</Button>
      </Box>
    </Box>
  );

  const renderMcpToolsContent = () => (
    <Box sx={{ width: '100%', maxWidth: 1200 }}>
      <Typography component="h2" sx={{ ...typographyStyles.h2, mb: 2, color: themeColors[theme].textBody }}>MCP Tools</Typography>
      <Typography sx={{ ...typographyStyles.body, mb: 6, color: themeColors[theme].textBody }}>
        All available tools exposed by the Availity MCP Server, grouped by category.
      </Typography>
      {mcpToolCategories.map((cat) => (
        <Box key={cat.id} id={cat.id} sx={{ mb: 8 }}>
          <Typography component="h3" sx={{ ...typographyStyles.h3, mb: 4, color: themeColors[theme].textSubheading }}>{cat.category}</Typography>
          <Box className="ai-management-tab__tools-list">
            {cat.tools.map((tool) => (
              <Box key={tool.name} className="ai-management-tab__tool-item">
                {/* Tool heading */}
                <Box className="ai-management-tab__tool-heading">
                  {/* Step row */}
                  <Box className="ai-management-tab__tool-step-row">
                    <Box className="ai-management-tab__tool-step-badge" sx={{ bgcolor: themeColors[theme].cardBackground }}>
                      <Typography sx={{ ...typography.latoBold16, color: themeColors[theme].text }}>
                        {cat.tools.indexOf(tool) + 1}
                      </Typography>
                    </Box>
                    <Typography sx={{ ...typography.montSemibold20, color: themeColors[theme].textSubheading }}>
                      {tool.name}
                    </Typography>
                  </Box>
                  {/* Proof points */}
                  <Box className="ai-management-tab__tool-proof-points">
                    {/* description - What it Does */}
                    <Box className="ai-management-tab__tool-proof-item">
                      <Box className="ai-management-tab__tool-proof-row">
                        <Box className="ai-management-tab__tool-proof-icon">
                          <Box component="img" src={checkOrangeIcon} sx={{ width: 18, height: 18 }} />
                        </Box>
                        <Typography sx={{ ...typography.latoRegular18, color: themeColors[theme].text }}>
                          <Box component="span" sx={{ fontWeight: 700 }}>What it Does: </Box>{tool.description}
                        </Typography>
                      </Box>
                      <Box className="ai-management-tab__tool-divider" sx={{ bgcolor: themeColors[theme].border }} />
                    </Box>
                    {/* use_case - When to Use */}
                    <Box className="ai-management-tab__tool-proof-item">
                      <Box className="ai-management-tab__tool-proof-row">
                        <Box className="ai-management-tab__tool-proof-icon">
                          <Box component="img" src={checkOrangeIcon} sx={{ width: 18, height: 18 }} />
                        </Box>
                        <Typography sx={{ ...typography.latoRegular18, color: themeColors[theme].text }}>
                          <Box component="span" sx={{ fontWeight: 700 }}>When to Use: </Box>{tool.use_case}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Parameters table */}
                {tool.parameters.length > 0 && (
                  <Grid>
                    <Typography sx={{ ...typography.montSemibold16, color: themeColors[theme].text, mb: 2 }}>Parameters</Typography>
                    <Grid className="ai-management-tab__tool-params" sx={{ border: `1px solid ${themeColors[theme].border}` }}>
                      {/* Table header */}
                      {(() => {
                        const hasNested = tool.parameters.some((p) => p.properties?.length);
                        const cols = hasNested ? ['Name', 'Type', 'Description'] : ['Name', 'Type', 'Description'];
                        return (
                          <>
                            <Grid className="ai-management-tab__param-table-header" sx={{ bgcolor: themeColors[theme].cardBackground, borderBottom: `1px solid ${themeColors[theme].border}` }}>
                              {cols.map((col, i) => (
                                <Grid key={col} className={`ai-management-tab__param-table-header-cell ai-management-tab__param-table-header-cell--${col === 'Description' ? 'desc' : col === 'Type' ? 'type' : 'name'}`} sx={{ borderRight: i < cols.length - 1 ? `1px solid ${themeColors[theme].border}` : 'none', ...(col === 'Name' && hasNested ? { paddingLeft: '48px' } : {}) }}>
                                  <Typography sx={{ ...typography.latoSemibold14, color: themeColors[theme].text }}>{col}</Typography>
                                </Grid>
                              ))}
                            </Grid>
                            {/* Table rows */}
                            <ParameterRows params={tool.parameters} hasNested={hasNested} />
                          </>
                        );
                      })()}
                    </Grid>
                  </Grid>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );

  const handleScrollSync = useCallback((contentId: string) => {
    setActiveContent(contentId);
  }, []);

  const sidebarProps = {
    expandedSection,
    activeSection,
    activeContent,
    onToggleSection: handleToggleSection,
    onMenuItemClick: handleMenuItemClick,
    onScrollSync: handleScrollSync,
    theme,
    mcpToolCategories,
    isScrollingRef: isProgrammaticScrollRef,
  };

  return (
    <Box sx={{ display: 'flex', bgcolor: themeColors[theme].background, fontFamily: 'Lato' }}>
      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
        <Sidebar {...sidebarProps} />
      </Box>
      <Drawer
        anchor="left"
        open={sidebarOpen}
        onClose={onSidebarClose}
        sx={{ display: { xs: 'block', lg: 'none' }, '& .MuiDrawer-paper': { width: 280 } }}
      >
        <Sidebar
          {...sidebarProps}
          onMenuItemClick={(id) => {
            handleMenuItemClick(id);
            onSidebarClose();
          }}
          onToggleSection={(section) => {
            handleToggleSection(section);
            onSidebarClose();
          }}
        />
      </Drawer>

      <Box
        ref={contentBoxRef}
        sx={{
          flex: 1,
          minWidth: 0,
          p: mcpError && activeSection !== 'mcp-server' ? 0 : { xs: '24px 16px', md: '48px 32px', lg: '48px 64px' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        {activeSection === 'mcp-server' ? (
          renderMcpServerContent()
        ) : (
          <ErrorBoundary theme={theme}>
            <McpToolsErrorThrower error={mcpError} />
            {mcpLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress size={32} sx={{ color: colors.text.secondary }} />
              </Box>
            ) : renderMcpToolsContent()}
          </ErrorBoundary>
        )}
      </Box>
    </Box>
  );
};

export { default as Sidebar } from './Sidebar';

export default AIManagementTab;
